import {Component, EventEmitter, Input, Output, ViewEncapsulation} from '@angular/core';
import {
    GenericChartTileConfiguration,
    SeriesSummaryChartTileConfiguration
} from "../../charts/chart-config/generic-chart-tile.configuration";
import * as utils from "../../lib/utils";
import {SeriesDataService} from "../../services/series_data.service";
import {DateTimeInstanceService} from "../../services/date-time-instance.service";
import {BaseComponent} from "../../shared/base.component";
import {takeUntil, tap} from "rxjs/operators";
import {ProgressTrackerChartService} from "../../charts/progress-tracker-chart/progress-tracker-chart.service";
import {ChartSeriesConfiguration} from "../../charts/chart-config/chart-series.configuration";
import {COMMON_FORM_IMPORTS} from "../../shared/import-groups/Common";
import {SelectFilterComponent} from "../../components/select-filter/select-filter.component";
import {
    SeriesSummaryColumnSelectorComponent
} from "../../components/series-summary-column-selector/series-summary-column-selector.component";
import {
    CustomChartFormSeriesTableComponent
} from "../custom-chart-form/custom-chart-form-series-table/custom-chart-form-series-table.component";


@Component({
    selector: 'progress-tracker-chart-form',
    templateUrl: './progress-tracker-chart-form.component.html',
    styleUrl: './progress-tracker-chart-form.component.scss',
    imports: [
    COMMON_FORM_IMPORTS,
    SelectFilterComponent,
    SeriesSummaryColumnSelectorComponent,
    CustomChartFormSeriesTableComponent
],
    encapsulation: ViewEncapsulation.None
})
export class ProgressTrackerChartFormComponent extends BaseComponent {
    @Input() config: SeriesSummaryChartTileConfiguration;
    colFilter: string;
    allColumns: any;
    periodTypes: string[] = ['Week', 'Month', 'Quarter', 'Year'];
    estimateTypes: string[];
    colMenuOptions: string[] = ['bold', 'size', 'colour', 'background_colour', 'decimals'];
    @Output() configChange: EventEmitter<SeriesSummaryChartTileConfiguration> = new EventEmitter();

    constructor(private seriesData: SeriesDataService,
                private dateInst: DateTimeInstanceService,
                private progressTrackerService: ProgressTrackerChartService) {
        super();
    }

    ngOnInit(): void {
        this.seriesData.estimateTypesChanged.pipe(takeUntil(this.onDestroy), tap(response => {
            this.estimateTypes = response.map(e => e.attributes.name);
        })).subscribe();

        if (!Object.keys(this.config)?.length) {
            this.config = GenericChartTileConfiguration.newProgressTrackerChart();
        }
        this.colFilter = utils.deepCopy(this.config.estimate_type);

        // Initialize columns if estimate_type is set but columns are empty
        if (this.config.estimate_type && (!this.config.columns || this.config.columns.length === 0)) {
            this.updateColumns();
        }
    }

    updatePeriodType(event) {
        this.config.period_type = event;
        this.updateColumns();
    }


    updateColumns(event?) {
        if (event) this.config.estimate_type = event;

        this.allColumns = this.seriesData.fillColumns([this.config.estimate_type]);
        this.config.columns = this.progressTrackerService.getSelectedCols(this.config);

        //Remove the old estimate_type ones from the selected_list
        if (this.colFilter && this.colFilter !== this.config.estimate_type) {
            this.config.columns = this.seriesData.filterColumns(this.config.columns, [this.colFilter]);
        }
        this.colFilter = utils.deepCopy(this.config.estimate_type);

        // Initialize column_formats with default titles for new columns
        this.initializeColumnFormats();

        this.configChange.next(this.config);
    }

    private initializeColumnFormats() {
        if (!this.config.column_formats) {
            this.config.column_formats = {};
        }

        const abbr = this.getPeriodShortName();
        const fullPeriod = `Full ${this.config.period_type} ${this.config.estimate_type}`;
        const fullPeriodVariance = `${fullPeriod} Variance`;

        this.config.columns.forEach(col => {
            if (!this.config.column_formats[col]) {
                this.config.column_formats[col] = {};
            }

            // Set default title if not already set
            if (!this.config.column_formats[col].title) {
                if (col === abbr) {
                    this.config.column_formats[col].title = 'Cumulative';
                } else if (col === 'Daily') {
                    this.config.column_formats[col].title = 'Yesterday';
                } else if (col === fullPeriod) {
                    this.config.column_formats[col].title = 'Remaining';
                } else if (col === fullPeriodVariance) {
                    this.config.column_formats[col].title = 'Over Budget';
                }
            }
        });
    }

    private getPeriodShortName(): string {
        return this.config.period_type === 'Week' ? 'WTD' :
            this.config.period_type === 'Month' ? 'MTD' :
                this.config.period_type === 'Quarter' ? 'QTD' : 'YTD';
    }

}
